import React, { useState, useEffect } from "react";
import { Button, Input, Tooltip } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useDebounce } from "hooks/commons/usePaginationTable";

interface ColumnFilterHeaderProps {
  title: string;
  dataIndex: string;
  activeFilter?: string;
  onFilterChange: (dataIndex: string, value: string) => void;
  allowSearch: boolean;
}

export const ColumnFilterHeader: React.FC<ColumnFilterHeaderProps> = ({
  title,
  dataIndex,
  activeFilter,
  onFilterChange,
  allowSearch,
}) => {
  const [showInput, setShowInput] = useState(false);
  const [inputValue, setInputValue] = useState(activeFilter || "");

  // Usar debounce para enviar el filtro
  const debouncedValue = useDebounce(inputValue, 300);

  // Enviar filtro cuando el valor debounced cambia
  useEffect(() => {
    onFilterChange(dataIndex, debouncedValue.trim());
  }, [debouncedValue, dataIndex, onFilterChange]);

  // Actualizar valor cuando cambia el filtro activo
  useEffect(() => {
    setInputValue(activeFilter || "");
  }, [activeFilter]);

  // Mostrar input automáticamente si hay filtro activo
  useEffect(() => {
    if (activeFilter && !showInput) {
      setShowInput(true);
    }
  }, [activeFilter, showInput]);

  const handleFilterIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowInput(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputClear = () => {
    setInputValue("");
    setShowInput(false);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      handleInputClear();
    }

    // Prevenir que AntD capture Enter para ordenamiento
    if (e.key === "Enter") {
      e.stopPropagation();
    }
  };

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Mostrar solo input si showInput es true o hay filtro activo
  if (showInput || activeFilter) {
    return (
      <div
        className="column-filter-header"
        onClick={(e) => e.stopPropagation()}
        style={{ width: "100%" }}
      >
        <div style={{ marginBottom: "4px" }}>{title}</div>
        <Input
          size="small"
          placeholder={`Buscar ${title.toLowerCase()}...`}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onClick={handleInputClick}
          allowClear
          onClear={handleInputClear}
          style={{
            width: "100%",
          }}
        />
      </div>
    );
  }

  // Mostrar solo icono de lupa
  return (
    <div
      className="column-filter-header"
      onClick={(e) => e.stopPropagation()}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        minHeight: "32px",
        width: "100%",
        cursor: "pointer",
      }}
    >
      <span
        style={{
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {title}
      </span>
      {allowSearch && (
        <Tooltip title="Filtrar columna">
          <Button
            shape="circle"
            icon={<SearchOutlined />}
            onClick={handleFilterIconClick}
          />
        </Tooltip>
      )}
    </div>
  );
};
